import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
  inject
} from '@angular/core';
import type { ChartDataEntry, ChartDataSeries, ChartLegendOptions } from '../common';
import { FireflyBaseChartComponent } from '../common/base-components/base-chart-component';
import { FireflyLineChartMobilePopoverComponent } from '../common/mobile-popovers/line-chart-popover/line-chart-mobile-popover.component';
import { FireflyChartBoilerplateComponent } from '../common/chart-boilerplate';
import { Breakpoint } from '../../utils';
import { FireflyModalService } from '../../modal';
import { ModalRef } from '../../modal/models/base-modal-options';
import { FireflyLocalizationService } from '../../utils';
import { take } from 'rxjs/operators';

@Component({
  selector: 'f-line-chart',
  templateUrl: './line-chart.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class FireflyLineChartComponent extends FireflyBaseChartComponent implements OnChanges, OnInit {
  @ViewChild(FireflyChartBoilerplateComponent)
  private chartBoilerplate?: FireflyChartBoilerplateComponent;

  @Input() data!: ChartDataSeries[];
  @Input() lineCurved = false;
  @Input() lineWidth = 2;
  @Input() circleRadius = 4;
  /**
   * Default line series palette when no `lineClasses` input is provided.
   * Keeps legend markers and line colors in sync with the Firefly demo palette.
   */
  @Input() lineClasses: string[] = [];
  @Input() showPointerLine = false;

  pointerXCoordinate!: number | undefined;
  activeDataPointId = -1;

  chartLegendOptions!: ChartLegendOptions;

  private readonly modalService = inject(FireflyModalService);
  private readonly localizationService = inject(FireflyLocalizationService, { optional: true });
  private mobileModal: ModalRef<unknown> | null = null;
  private modalTitle = 'Chart Details';

  ngOnInit(): void {
    this.localizationService
      ?.localize('chartDetailTitle', {})
      .pipe(take(1))
      .subscribe((title: string) => {
        this.modalTitle = title;
      });
  }
  override ngOnChanges(changes?: SimpleChanges): void {
    super.ngOnChanges();
    // Populate defaults so legend/color processing works even when inputs are omitted.
    this.ensureDefaultColors();
    if (!this.legendOptions?.data?.length) return;
    if (changes && !changes['legendOptions'] && !changes['data'] && !changes['lineClasses']) return;

    this.chartLegendOptions = {
      ...this.legendOptions,
      data: this.legendOptions.data.map((item, index) => {
        const seriesClass = this.getSeriesCssClassByLabel(item.label);
        const markerCssClass = this.getLegendMarkerClass(
          item.markerCssClass ?? '',
          seriesClass,
          this.getLegendColorClass(index)
        );
        return markerCssClass === item.markerCssClass ? item : { ...item, markerCssClass };
      })
    };
  }

  get lineSeriesData(): ChartDataSeries[] {
    // Expose series data for the line-series renderer.
    return this.data || [];
  }

  get lineSeriesClasses(): string[] {
    // Provide class list per rendered line series.
    return this.lineClasses;
  }

  onPointerMove(event: MouseEvent) {
    // Track pointer position and update nearest data point for hover state.
    if (!this.interactions?.hover) return;
    const pointer = this.getPointerDataFromEvent(event);
    if (!pointer) return;

    this.pointerXCoordinate = pointer.x;
    if (pointer.index === this.activeDataPointId) return;
    this.activeDataPointId = pointer.index;
    this.cdr.detectChanges();
  }

  @HostListener('mouseleave') onMouseLeave() {
    // Reset hover state when leaving the chart on non-mobile sizes.
    if (window.innerWidth < Breakpoint.Sm) return;
    this.pointerXCoordinate = undefined;
    this.activeDataPointId = -1;
    this.cdr.detectChanges();
  }

  private getNearestDataPointIndex(x: number): number | null {
    // Find the closest data point index to the pointer x position.
    const names = (this.data || []).map(entry => entry.name);
    let minDiff = Number.MAX_VALUE;
    let nearestIndex = -1;

    names.forEach((name, index) => {
      const scaled = this.xScale(name);
      if (scaled === undefined) return;
      const diff = Math.abs(x - scaled);
      if (diff < minDiff) {
        minDiff = diff;
        nearestIndex = index;
      }
    });

    return nearestIndex >= 0 ? nearestIndex : null;
  }

  protected getYDomain(data: ChartDataSeries[], startFromZero = true): number[] {
    // Compute y-axis domain from all series values, optionally including zero.
    const values = data
      .flatMap(entry =>
        (entry.series as { value?: number | null }[]).map(item => item.value).filter(value => typeof value === 'number')
      )
      .filter(value => typeof value === 'number') as number[];

    const adjustedValues = startFromZero ? [0, ...values] : values;
    if (!adjustedValues.length) return [0, 0];
    const min = Math.min(...adjustedValues);
    const max = Math.max(...adjustedValues);
    return [min, max];
  }

  private getSeriesCssClassByLabel(label: string): string {
    const labels = this.getSeriesLabels();
    const index = labels.indexOf(label);
    if (index < 0) return '';
    return this.lineClasses?.[index] ?? '';
  }

  private getLegendMarkerClass(markerCssClass: string, seriesClass: string, colorClass: string): string {
    let nextClass = markerCssClass ?? '';
    if (seriesClass.includes('dashed-line') || seriesClass.includes('f-dashed-line')) {
      nextClass = this.appendClass(nextClass, 'dashed-line');
    }
    if (colorClass) {
      nextClass = this.appendClass(nextClass, colorClass);
    }
    return nextClass;
  }

  /**
   * Map line border classes to text classes so legend dashed-line uses currentColor.
   */
  private getLegendColorClass(index: number): string {
    const lineClass = this.lineClasses?.[index] ?? '';
    if (!lineClass) return '';
    if (lineClass.startsWith('border-')) {
      return `text-${lineClass.substring('border-'.length)}`;
    }
    return '';
  }

  private ensureDefaultColors(): void {
    // Apply default line colors and legend entries when caller leaves them unset.
    if (!this.lineClasses || !this.lineClasses.length) {
      this.lineClasses = this.defaultLineClasses;
    }

    if (!this.legendOptions || !this.legendOptions.data?.length) {
      const defaultLegendOptions = this.buildDefaultLegendOptions();
      if (defaultLegendOptions) {
        this.legendOptions = defaultLegendOptions;
      }
    }
  }

  private buildDefaultLegendOptions(): ChartLegendOptions | null {
    // Build legend from series labels so demos still show legend colors without inputs.
    const labels = this.getSeriesLabels();
    if (!labels.length) return null;

    return {
      data: labels.map((label, index) => ({
        label,
        markerCssClass: `legend-line ${this.getDefaultLegendMarkerClass(index)}`
      })),
      legendContentAlignment: 'center',
      legendPosition: 'bottom',
      legendItemsMarginSize: 3
    };
  }

  private getSeriesLabels(): string[] {
    const firstEntry = this.data?.[0];
    if (!firstEntry || !Array.isArray(firstEntry.series)) return [];
    return (firstEntry.series as { name?: string }[])
      .map(entry => entry.name)
      .filter((name): name is string => Boolean(name));
  }

  private getDefaultLegendMarkerClass(index: number): string {
    const token = this.defaultColorTokens[index % this.defaultColorTokens.length];
    return `bg-${token}`;
  }

  private get defaultLineClasses(): string[] {
    return this.defaultColorTokens.map(token => `border-${token}`);
  }

  private get defaultColorTokens(): string[] {
    return [
      'primary-600',
      'warning-600',
      'accent-2-600',
      'accent-5-500',
      'accent-1-600',
      'accent-6-400',
      'accent-4-600'
    ];
  }

  private appendClass(base: string, className: string): string {
    if (!className) return base;
    const tokens = `${base} ${className}`.split(/\s+/).filter(Boolean);
    return [...new Set(tokens)].join(' ');
  }

  /**
   * Opens the mobile popover for the nearest data point on tap.
   *
   * Behavior:
   * - Only runs on mobile and when mobile interactions are allowed.
   * - Reuses the desktop popover template/content.
   * - Creates a modal with a line-only chart (no bars) and the selected point data.
   */
  onPointerClick(event: MouseEvent): void {
    // Ignore clicks on desktop or when mobile popovers are disabled.
    if (this.interactions?.preventMobile || window.innerWidth >= Breakpoint.Sm) return;
    // Guard against missing template or an already-open modal.
    if (!this.popoverTemplate || this.mobileModal) return;

    // Resolve the nearest data point from the click position.
    const pointer = this.getPointerDataFromEvent(event);
    if (!pointer) return;
    const { index, x } = pointer;

    // Build the same popover payload used by the desktop line popover.
    const popoverData = this.buildMobilePopoverData(index);
    const chartBoilerplate = this.chartBoilerplate;
    if (!popoverData || !chartBoilerplate) return;

    // Persist selection so the pointer/legend state matches the tapped point.
    this.pointerXCoordinate = x;
    this.activeDataPointId = index;

    const mobileSlice = this.getMobileChartSlice(index);
    const chartData = this.data.slice(mobileSlice.start, mobileSlice.end);

    // Open the mobile modal and inject the line-chart context (no bar data).
    this.mobileModal = this.modalService.open({
      component: FireflyLineChartMobilePopoverComponent,
      modalDialogClass: 'f-chart-mobile-popover',
      context: this.getMobileModalContext(popoverData, chartData, mobileSlice.activeIndex, chartBoilerplate),
      title: this.modalTitle,
      mobile: true
    });

    // Clear modal ref on close and refresh view for pointer updates.
    this.mobileModal.result.finally(() => (this.mobileModal = null));
    this.cdr.detectChanges();
  }

  private buildMobilePopoverData(index: number): { name: string; series: ChartDataEntry[] } | null {
    const entry = this.data?.[index];
    if (!entry) return null;
    const labels = this.getSeriesLabels();
    if (!labels.length) return null;

    const legendClassMap = this.getLegendClassMap();
    const seriesEntries = (entry.series as ChartDataEntry[]) ?? [];
    const series = labels
      .map((label, seriesIndex) => {
        const dataEntry = seriesEntries.find(item => item?.name === label);
        if (!dataEntry) return null;
        const baseClass = this.lineClasses?.[seriesIndex] ?? '';
        const mergedClass = this.appendClass(baseClass, legendClassMap[label] ?? '');
        return { ...dataEntry, cssClass: mergedClass };
      })
      .filter(Boolean) as ChartDataEntry[];

    return { name: entry.name, series };
  }

  private getMobileModalContext(
    popoverData: { name: string; series: ChartDataEntry[] },
    chartData: ChartDataSeries[],
    activeDataPointId: number,
    chartBoilerplate: FireflyChartBoilerplateComponent
  ) {
    return {
      popoverData,
      template: this.popoverTemplate,
      chartData,
      activeDataPointId,
      chartBoilerplate,
      component: FireflyLineChartComponent,
      lineClasses: this.lineClasses,
      lineWidth: this.lineWidth,
      circleRadius: this.circleRadius,
      legendOptions: this.chartLegendOptions || this.legendOptions,
      showLegend: this.showLegend
    };
  }

  private getPointerDataFromEvent(event: MouseEvent): { x: number; index: number } | null {
    if (!this.xScale || !this.dimensions) return null;
    const target = event.currentTarget as SVGRectElement | null;
    if (!target) return null;
    const bounds = target.getBoundingClientRect();
    const x = event.clientX - bounds.left;
    const index = this.getNearestDataPointIndex(x);
    if (index === null) return null;
    return { x, index };
  }

  private getLegendClassMap(): Record<string, string> {
    const options = this.chartLegendOptions || this.legendOptions;
    if (!options?.data?.length) return {};
    return options.data.reduce((acc, item) => {
      if (item.label && item.markerCssClass) {
        acc[item.label] = item.markerCssClass;
      }
      return acc;
    }, {} as Record<string, string>);
  }

  private getMobileChartSlice(activeIndex: number): { start: number; end: number; activeIndex: number } {
    const total = this.data?.length ?? 0;
    if (total <= 1) return { start: 0, end: total, activeIndex: 0 };

    const windowSize = Math.min(3, total);
    let start = activeIndex - 1;
    let end = activeIndex + 2;

    if (start < 0) {
      start = 0;
      end = windowSize;
    } else if (end > total) {
      end = total;
      start = Math.max(0, total - windowSize);
    }

    return { start, end, activeIndex: activeIndex - start };
  }
}
